import { ReactElement, useMemo } from 'react';
import { ReactComponent as ArrowRightDownLine } from '../../../assets/icons/arrow-right-down-line-v2.svg';
import { ReactComponent as ArrowRightUpLine } from '../../../assets/icons/arrow-right-up-line-v2.svg';
import { useFormatter } from '../../services/utils/useFormatter';
import { cssWhen } from '../../utils/themeUtils';
import classes from './NbStatTagInline.module.scss';

export type NbStatTagInlineDirection = 'up' | 'down' | 'none';

export interface NbStatTagInlineProps {
  direction: NbStatTagInlineDirection;
  changePercentage?: number;
  changeValue?: number;
  className?: string;
}

export const NbStatTagInline = ({
  className,
  direction,
  changeValue,
  changePercentage,
}: NbStatTagInlineProps): ReactElement => {
  const { formatPercentageWithSign, formatNumberRoundOrF2 } = useFormatter();

  const directionIcon = useMemo(() => {
    if (direction === 'none') {
      return <span>-</span>;
    } else if (direction === 'up') {
      return <ArrowRightUpLine />;
    } else {
      return <ArrowRightDownLine />;
    }
  }, [direction]);

  const formattedChangeValue = useMemo(() => {
    if (changeValue === undefined) {
      return undefined;
    }
    return formatNumberRoundOrF2(changeValue);
  }, [changeValue, formatNumberRoundOrF2]);

  const formattedChangePercentage = useMemo(() => {
    if (changePercentage === undefined) {
      return undefined;
    }
    return formatPercentageWithSign(changePercentage);
  }, [changePercentage, formatPercentageWithSign]);

  return (
    <div
      className={cssWhen(
        className,
        classes.statTagInline,
        [direction === 'up', classes.statTagInlineIsUp],
        [direction === 'down', classes.statTagInlineIsDown],
        [direction === 'none', classes.statTagInlineIsNeutral],
      )}
    >
      <span
        className={classes.statTagInlineIcon}
        aria-hidden="true"
      >
        {directionIcon}
      </span>
      <div className={classes.statTagInlineText}>
        <span>{formattedChangeValue}</span>
        <span>({formattedChangePercentage})</span>
      </div>
    </div>
  );
};
